import { useState } from 'react';
import { APP_VERSION } from '../../version.generated';
import { LANGUAGES } from '../../i18n/catalogs';
import { useI18n } from '../../i18n/I18nProvider';

export interface LoginScreenProps {
  /** Any server credential error collapses to one generic message on purpose. */
  errorCode?: string | null;
  submitting?: boolean;
  version?: string;
  year?: number;
  onSubmit?(email: string, password: string): void;
  onOpenChangelog?(): void;
}

const FEATURE_KEYS = ['loginFeature1', 'loginFeature2', 'loginFeature3', 'loginFeature4'] as const;

export function LoginScreen({
  errorCode,
  submitting = false,
  version = APP_VERSION,
  year = new Date().getFullYear(),
  onSubmit,
  onOpenChangelog,
}: LoginScreenProps): JSX.Element {
  const { language, setLanguage, t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const hasError = errorCode !== undefined && errorCode !== null && errorCode !== '';

  return (
    <div className="auth" data-language={language}>
      <section className="auth__brand">
        <div className="auth__logo" aria-hidden="true">
          {t('appName')}
        </div>
        <h1 className="auth__brand-title">{t('loginBrandTitle')}</h1>
        <p className="auth__lead">{t('loginBrandLead')}</p>
        <ul className="auth__feats">
          {FEATURE_KEYS.map((key, index) => (
            <li className="auth__feat" key={key}>
              <span className="auth__feat-n">{index + 1}</span>
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
        <div className="auth__legal">
          <button type="button" className="btn btn--sm auth__version" onClick={onOpenChangelog}>
            {`v${version}`}
          </button>
          <span>{`© ${year} OptimaGroup`}</span>
        </div>
      </section>

      <section className="auth__side">
        <div className="auth__top">
          <div className="lang" role="group" aria-label={t('languageLabel')}>
            {LANGUAGES.map((code) => (
              <button
                key={code}
                type="button"
                className={code === language ? 'lang__btn is-active' : 'lang__btn'}
                aria-pressed={code === language}
                onClick={() => setLanguage(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <form
          className="auth__form"
          data-testid="login-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit?.(email, password);
          }}
        >
          <h2>{t('loginTitle')}</h2>
          <p className="auth__sub">{t('loginSubtitle')}</p>

          {hasError ? (
            <div className="alert alert--danger" role="alert" data-testid="login-error">
              {t('loginError')}
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="login-email">{t('loginEmail')}</label>
            <input
              id="login-email"
              className="input"
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="login-password">{t('loginPassword')}</label>
            <div className="input-wrap">
              <input
                id="login-password"
                className="input"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="eye"
                aria-label={showPassword ? t('loginHidePassword') : t('loginShowPassword')}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((current) => !current)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
                  <circle cx="12" cy="12" r="2.6" />
                </svg>
              </button>
            </div>
          </div>

          <button className="btn btn--primary auth__submit" type="submit" disabled={submitting}>
            {submitting ? t('loginSubmitting') : t('loginSubmit')}
          </button>

          <p className="auth__note">{t('loginFirstRunHint')}</p>
        </form>
      </section>
    </div>
  );
}
